import {
  getExtensionIdentifier,
  getOrGenerateIdentifier,
} from "./utils/identity-helpers";
import { setUpOnTabRemoveListeners } from "./background-script/tab-remove-listeners";
import { setUpBackgroundListeners } from "./listeners/listener-helpers-SW";
import { inIframe } from "./utils/iframe-helpers";
import { purgeOnStartup } from "./background-script/purge-on-startup";
import { setUpStorageChangeListeners } from "./content-script/storage-change-listeners";
import {
  isStarted,
  start,
  startWebsocket,
  stop,
} from "./utils/start-stop-helpers";
import { getOptInStatus, optIn, optOut } from "./utils/opt-in-out-helpers";
import { checkRequiredPermissions } from "./permissions/permission-helpers";
import { MAX_DAILY_RATE as DEFAULT_MAX_DAILY_RATE, VERSION } from "./constants";
import { Logger } from "./logger/logger";
import { RateLimiter } from "./local-rate-limiting/rate-limiter";
import { setUpExternalMessageListeners } from "./elements/web-platform";
import {
  generateOptInLink,
  generateSettingsLink,
  openUserSettingsInPopupWindow,
  generateAndOpenOptInLink,
  generateUpdateLink,
  generateAndOpenUpdateLink,
  generateFeedbackLink,
  generateAndOpenFeedbackLink,
} from "./elements/generate-links";
import { detectBrowser } from "./utils/utils";
import { switchShouldContinue } from "./switch/check-switch";
import { setLocalStorage } from "./storage/storage-helpers";
import { cleaunUpRules } from "./dnr/dnr-helpers";

export default class M {
  // `publishableKey` accepts two formats during the migration window:
  //   - Legacy configuration key (no prefix, e.g. "abc123"). Deprecated.
  //   - New integration id, prefixed with "intgr-" (e.g. "intgr-abc123"). Preferred.
  // Both are treated identically by the SDK; the prefix is only used to detect
  // the legacy form and emit a deprecation warning.
  private publishableKey: string;
  private options?: any;
  private disableLogs: boolean = true;
  private MAX_DAILY_RATE: number = DEFAULT_MAX_DAILY_RATE;

  constructor(publishableKey: string, options?: any) {
    this.publishableKey = publishableKey;
    this.options = options;
    this.disableLogs =
      options?.disableLogs !== undefined ? options.disableLogs : true;
    this.MAX_DAILY_RATE = options?.MAX_DAILY_RATE || DEFAULT_MAX_DAILY_RATE;
    RateLimiter.MAX_DAILY_RATE = this.MAX_DAILY_RATE;
    Logger.disableLogs = this.disableLogs;
  }

  public async initBackground(
    auto_start_if_opted_in?: boolean | undefined,
    metadata_id?: string | undefined,
  ): Promise<void> {
    if (
      typeof this.publishableKey === "undefined" ||
      this.publishableKey === null ||
      this.publishableKey === ""
    ) {
      throw new Error("publishableKey is undefined, null, or empty");
    }
    // Deprecation: legacy configuration keys lack the "intgr-" prefix.
    // Warn developers to migrate. Use console.warn directly so the notice
    // surfaces even when `disableLogs: true` is set.
    if (!this.publishableKey.startsWith("intgr-")) {
      console.warn(
        "[mellowtel] You are using a legacy configuration key. " +
          "Please migrate to an integration id (prefixed with 'intgr-')" +
          "Configuration keys will be deprecated soon. " +
          "See migration guide: https://docs.mellowtel.com/migrations/integration-id",
      );
    }
    await checkRequiredPermissions(false);
    await purgeOnStartup();
    await setUpOnTabRemoveListeners();
    await setUpBackgroundListeners();
    await getOrGenerateIdentifier(this.publishableKey);
    let shouldContinue: boolean = await switchShouldContinue();
    if (shouldContinue) {
      Logger.log("[initBackground]: Switch is on. Continuing.");
      if (auto_start_if_opted_in === undefined || auto_start_if_opted_in) {
        let optInStatus: boolean = (await getOptInStatus()).boolean;
        if (optInStatus) {
          await start(metadata_id);
          // keep service worker alive
        }
      }
    } else {
      Logger.log("[initBackground]: Switch is off. Not continuing.");
    }
  }

  public async initContentScript(options?: {
    pascoliFilePath?: string;
    meucciFilePath?: string;
  }): Promise<void> {
    if (options?.pascoliFilePath) {
      await setLocalStorage("mllwtl_pascoliFilePath", options.pascoliFilePath);
    }
    if (options?.meucciFilePath) {
      await setLocalStorage("mllwtl_meucciFilePath", options.meucciFilePath);
    }
    if (typeof window !== "undefined") {
      await setUpExternalMessageListeners();
      await cleaunUpRules(); // clean up rules every page load
    }
    if (typeof window !== "undefined") {
      if (inIframe()) {
        const mutationObserverModule = await import(
          "./iframe/mutation-observer"
        );
        mutationObserverModule.listenerAlive();
        mutationObserverModule.attachMutationObserver();
      } else {
        let shouldContinue: boolean = await switchShouldContinue();
        if (shouldContinue) {
          Logger.log("[initContentScript]: Switch is on. Continuing.");

          if ((await isStarted()) && (await getOptInStatus())) {
            startWebsocket();
          } else {
            await setUpStorageChangeListeners();
          }
        } else {
          Logger.log("[initContentScript]: Switch is off. Not continuing.");
        }
      }
    }
  }

  public async optIn(): Promise<boolean> {
    return optIn();
  }

  public async optOut(): Promise<boolean> {
    return optOut();
  }

  public async getOptInStatus(): Promise<boolean> {
    return (await getOptInStatus()).boolean;
  }

  public async generateOptInLink(): Promise<string> {
    return generateOptInLink();
  }

  public async generateAndOpenOptInLink(): Promise<string> {
    return generateAndOpenOptInLink();
  }

  public async generateSettingsLink(): Promise<string> {
    return generateSettingsLink();
  }

  public async generateUpdateLink(): Promise<string> {
    return generateUpdateLink();
  }

  public async generateAndOpenUpdateLink(): Promise<string> {
    return generateAndOpenUpdateLink();
  }

  public async openUserSettingsInPopupWindow(): Promise<boolean> {
    return openUserSettingsInPopupWindow();
  }

  public async getNodeId(): Promise<string> {
    return getOrGenerateIdentifier(this.publishableKey);
  }

  public async getVersion(): Promise<string> {
    return VERSION;
  }

  public async getExtensionIdentifier(): Promise<string> {
    return getExtensionIdentifier();
  }

  public async getBrowser(): Promise<string> {
    return detectBrowser();
  }

  public async start(metadata_id?: string | undefined): Promise<boolean> {
    return start(metadata_id);
  }

  public async stop(): Promise<boolean> {
    return stop();
  }

  public async generateFeedbackLink(): Promise<string> {
    return generateFeedbackLink();
  }

  public async generateAndOpenFeedbackLink(): Promise<string> {
    return generateAndOpenFeedbackLink();
  }
}
